<template>
  <!-- Edit a user from the Users page: a side panel, so the list stays visible (US 9365) -->
  <b-modal ref="panel"
           modal-class="us-drawer"
           hide-header
           hide-footer
           @hidden="reset"
  >
    <div v-if="user" class="us-drawer__inner" qid="edit-user-panel">
      <header class="us-drawer__head">
        <span class="us-avatar us-avatar--lg" :style="{ backgroundImage: 'url(' + avatarUrl + ')' }"></span>
        <div class="us-drawer__who">
          <h2 class="us-drawer__name" qid="edit-user-name">{{ user.name }}</h2>
          <div class="us-drawer__meta">
            {{ user.email }}<span v-if="user.phone"> · {{ user.phone }}</span>
          </div>
          <label v-if="canChangePhoto" class="us-link us-drawer__photo" qid="edit-user-photo">
            <font-awesome-icon icon="cloud-upload-alt"/>
            {{ user.avatar ? $t('users_page.edit.change_photo') : $t('users_page.edit.upload_photo') }}
            <input type="file"
                   accept="image/png,image/jpeg,image/gif"
                   class="d-none"
                   @change="uploadPhoto">
          </label>
        </div>
        <button type="button"
                class="us-icon-btn us-drawer__close"
                :aria-label="$t('administration.users.create.cancel')"
                @click="close">
          <font-awesome-icon icon="times"/>
        </button>
      </header>

      <div class="us-drawer__body">
        <b-alert v-if="error"
                 show
                 variant="danger"
                 qid="edit-user-error">{{ error }}</b-alert>

        <section class="us-drawer__section">
          <div class="us-status">
            <template v-if="user.last_login_at">
              <span class="v2-tag v2-tag--teal"><font-awesome-icon icon="check"/>{{ $t('users_page.edit.signed_in') }}</span>
              <span class="us-status__text">{{ $t('users_page.edit.last_login', { date: formatDateTime(user.last_login_at) }) }}</span>
            </template>
            <template v-else>
              <InviteStatus :invite="user.invite"/>
              <span class="us-status__text">
                <span v-if="user.invite">{{ $t('users_page.edit.invite_sent', { date: formatDateTime(user.invite.created_at) }) }}</span>
                <span v-else>{{ $t('users_page.invite_hint.none') }}</span>
              </span>
              <button v-if="canResend"
                      type="button"
                      class="us-link"
                      qid="edit-user-resend"
                      @click="$emit('resend', user)"
              >
                <font-awesome-icon icon="paper-plane"/>{{ $t('users_page.resend_invite') }}
              </button>
            </template>
          </div>
        </section>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('users_page.edit.section_sign_in') }}</h3>
          <SignInMethodPicker v-model="form.authentication" :disabled="!user.can_change_authentication"/>
          <div v-if="!user.can_change_authentication" class="us-note us-note--info" qid="edit-user-sign-in-locked">
            <font-awesome-icon icon="info-circle"/>
            <span>{{ $t('users_page.edit.sign_in_locked') }}</span>
          </div>
          <b-form-checkbox v-if="switchesToPassword"
                           v-model="form.send_password_email"
                           qid="edit-user-send-password-email"
          >
            {{ $t('users_page.edit.send_password_email') }}
          </b-form-checkbox>
          <!-- UPN is rarely needed and only used for Microsoft sign-in, so it stays folded -->
          <template v-if="form.authentication === 'Microsoft'">
            <button type="button"
                    class="us-fold"
                    :aria-expanded="upnOpen ? 'true' : 'false'"
                    qid="edit-user-upn-toggle"
                    @click="upnOpen = !upnOpen"
            >
              <font-awesome-icon :icon="upnOpen ? 'angle-down' : 'angle-right'" fixed-width/>
              {{ $t('users_page.fields.upn_toggle') }}
              <span v-if="!upnOpen && form.upn_email" class="us-fold__value">{{ form.upn_email }}</span>
            </button>
            <b-form-group v-if="upnOpen" class="mb-0">
              <b-form-input v-model="form.upn_email"
                            type="email"
                            :maxLength="255"
                            :placeholder="user.email"
                            :disabled="!user.can_change_authentication"
                            :aria-label="$t('users_page.fields.upn')"
                            qid="edit-user-upn"
              ></b-form-input>
              <small class="us-hint">{{ $t('users_page.fields.upn_hint') }}</small>
            </b-form-group>
          </template>
        </section>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('users_page.edit.section_workspace') }}</h3>
          <b-form-group class="mb-0">
            <label class="us-field-label">{{ $t('users_page.fields.role') }}</label>
            <multiselect v-model="selectedRole"
                         :options="roles"
                         track-by="id"
                         label="name_translated"
                         :allow-empty="false"
                         :placeholder="$t('administration.clients.members.select_role_placeholder')"
                         :select-label="$t('system.dropdown_select_label')"
                         :selected-label="$t('system.dropdown_selected_label')"
                         :deselect-label="$t('system.dropdown_deselect_label')"
                         qid="edit-user-role"
            ></multiselect>
          </b-form-group>
          <b-alert v-if="selectedRole && (selectedRole.name === 'Client admin' || selectedRole.name === 'Partition admin')"
                   show
                   variant="warning"
                   class="mb-0"
          >
            {{ $t('administration.users.create.role_cost_notice') }}
          </b-alert>
        </section>

        <!-- Department and DPO are rarely used, so they stay folded with a one-line summary -->
        <section class="us-drawer__section">
          <button type="button"
                  class="us-fold us-fold--section"
                  :aria-expanded="organisationOpen ? 'true' : 'false'"
                  qid="edit-user-organisation-toggle"
                  @click="organisationOpen = !organisationOpen"
          >
            <font-awesome-icon :icon="organisationOpen ? 'angle-down' : 'angle-right'" fixed-width/>
            <span class="us-drawer__label">{{ $t('users_page.edit.section_organisation') }}</span>
            <span v-if="!organisationOpen" class="us-fold__value">{{ organisationSummary }}</span>
          </button>
          <template v-if="organisationOpen">
            <div>
              <label class="us-field-label">{{ $t('users_page.fields.department') }}</label>
              <OrganizationTreeView :organization="selectedOrganization" @updatedCompany="selectOrganization"></OrganizationTreeView>
            </div>
            <div class="us-switch-row">
              <span>{{ $t('users_page.fields.dpo') }}</span>
              <b-form-checkbox v-model="form.dpo"
                               switch
                               value="1"
                               unchecked-value="0"
                               qid="edit-user-dpo"
              ></b-form-checkbox>
            </div>
          </template>
        </section>
      </div>

      <footer class="us-drawer__foot">
        <button v-if="canRemove"
                type="button"
                class="us-link us-link--danger"
                qid="edit-user-remove"
                @click="$emit('remove', user)"
        >
          {{ $t('users_page.actions.remove') }}
        </button>
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="edit-user-cancel-button" @click="close">
          {{ $t('administration.users.create.cancel') }}
        </b-button>
        <b-button variant="success"
                  :disabled="saving"
                  qid="edit-user-submit-button"
                  @click="save">
          <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>
          {{ $t('users_page.edit.save') }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import {userAvatarUrl} from '@/util/userAvatar.js';
  import OrganizationTreeView from '@/components/organizations/OrganizationTreeView.vue';
  import InviteStatus from '@/components/user/InviteStatus.vue';
  import SignInMethodPicker from '@/components/user/SignInMethodPicker.vue';

  export default {
    name: 'UserEditPanel',
    components: {
      OrganizationTreeView,
      InviteStatus,
      SignInMethodPicker
    },
    props: {
      roles: Array,
      canResend: Boolean,
      canRemove: Boolean
    },
    data() {
      return {
        user: null,
        form: {},
        selectedRole: null,
        selectedOrganization: {},
        error: '',
        saving: false,
        upnOpen: false,
        organisationOpen: false
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      avatarUrl() {
        return userAvatarUrl(this.user, true);
      },
      isMe() {
        return this.user && this.$store.state.user && this.user.id === this.$store.state.user.id;
      },
      // The picture is global, like the sign-in method
      canChangePhoto() {
        return this.isMe || this.user.can_change_authentication;
      },
      switchesToPassword() {
        return this.form.authentication === 'Application' && (this.user.authentication || 'Application') !== 'Application';
      },
      // Shown on the folded Organisation section, e.g. "Finance · DPO"
      organisationSummary() {
        let parts = [this.selectedOrganization && this.selectedOrganization.name ? this.selectedOrganization.name : this.$t('users_page.edit.no_department')];

        if (this.form.dpo === '1') {
          parts.push(this.$t('users_page.log.fields.dpo'));
        }

        return parts.join(' · ');
      }
    },
    methods: {
      open(user, role) {
        this.user = Object.assign({}, user);
        this.form = {
          authentication: user.authentication || 'Application',
          upn_email: user.upn_email || '',
          dpo: user.dpo ? '1' : '0',
          organization: user.organization ? user.organization.id : '',
          send_password_email: true
        };
        this.selectedRole = role ? Object.assign({}, role) : null;
        this.selectedOrganization = user.organization || {};
        this.upnOpen = false;
        this.organisationOpen = false;
        this.error = '';
        this.$refs.panel.show();
      },
      close() {
        this.$refs.panel.hide();
      },
      reset() {
        this.user = null;
        this.saving = false;
      },
      selectOrganization(item) {
        this.form.organization = item && item.id ? item.id : '';
        this.selectedOrganization = item && item.id ? item : {};
      },
      formatDateTime(date) {
        return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
      },
      save() {
        let self = this;
        this.saving = true;
        this.error = '';

        let data = {
          client: this.getClient.slug,
          role: this.selectedRole ? this.selectedRole.id : undefined,
          organization: this.form.organization,
          dpo: this.form.dpo
        };

        if (this.user.can_change_authentication) {
          data.authentication = this.form.authentication;
          // The UPN only matters for Microsoft sign-in; other methods leave it as it is
          if (this.form.authentication === 'Microsoft') {
            data.upn_email = this.form.upn_email;
          }
          data.send_password_email = this.switchesToPassword && this.form.send_password_email ? 1 : 0;
        }

        piincHttp.put('users/' + this.user.slug, data).then(function() {
          self.saving = false;
          self.$toastr('success', self.$t('administration.users.update.success'));
          self.$emit('saved');
          self.close();
        }, function(error) {
          self.saving = false;
          self.error = self.errorMessage(error);
        });
      },
      uploadPhoto(event) {
        let self = this;
        let file = event.target.files[0];
        event.target.value = '';

        if (!file) {
          return;
        }
        if (file.size > 10485760) {
          this.error = this.$t('system.file_size');
          return;
        }

        let formData = new FormData();
        formData.append('avatar', file);
        formData.append('client', this.getClient.slug);
        formData.append('_method', 'put');

        piincHttp.post('users/' + this.user.slug, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).then(function(response) {
          self.user = Object.assign({}, self.user, { avatar: response.data.avatar, avatar_large: response.data.avatar_large });
          self.$emit('saved');
        }, function(error) {
          self.error = self.errorMessage(error);
        });
      },
      errorMessage(error) {
        let message = error && error.data && error.data.message ? error.data.message : '';

        if (error && error.status === 403 && message.indexOf('outside your organisation') !== -1) {
          return this.$t('users_page.edit.sign_in_locked');
        }
        if (message === 'You are not allowed to add member. Please check subscription.') {
          return this.$t('administration.users.create.subscription_error_member');
        }
        if (message === 'You are not allowed to add admin. Please check subscription.' || message === 'You are not allowed to add partition admin. Please check subscription.') {
          return this.getClient.parent
            ? this.$t('administration.users.create.subscription_error_partition_admin')
            : this.$t('administration.users.create.subscription_error_admin');
        }
        if (message === 'You are not allowed to add validator. Please check subscription.') {
          return this.$t('administration.users.create.subscription_error_validator');
        }

        return message || this.$t('users_page.error');
      }
    }
  }
</script>
